import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar, Download, User, FileText, Clock, CheckCircle, XCircle, Building2, GraduationCap, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const PresenceEntreprise = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [presences, setPresences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLieu, setFilterLieu] = useState('tous');

  useEffect(() => {
    loadStagiaires();
  }, []);

  useEffect(() => {
    if (selectedStagiaire) {
      loadPresences();
    }
  }, [selectedStagiaire]);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      const stagiairesList = response.data.filter(s => s.statut_validation !== 'supprime');
      setStagiaires(stagiairesList);
      if (stagiairesList.length > 0) {
        setSelectedStagiaire(stagiairesList[0].user_id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadPresences = async () => {
    try {
      const response = await axios.get(`/presences/stagiaire/${selectedStagiaire}`);
      setPresences(response.data);
    } catch (error) {
    }
  };

  const filteredPresences = filterLieu === 'tous' 
    ? presences 
    : presences.filter(p => (p.lieu || 'centre') === filterLieu);

  const downloadPresenceIndividuelle = async (format) => {
    if (!selectedStagiaire) return;
    try {
      const endpoint = format === 'pdf' 
        ? `/pdf/feuille-presence-entreprise/${selectedStagiaire}`
        : `/excel/feuille-presence-entreprise/${selectedStagiaire}`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
      const stagNom = `${stag?.user_info?.prenom || ''} ${stag?.user_info?.nom || ''}`.trim();
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stag?.user_info?.nom || 'stagiaire'}_${stag?.user_info?.prenom || ''}_presence.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Feuille ${format.toUpperCase()} téléchargée`);
      
      await axios.post('/feuilles-presence/historique', null, {
        params: {
          type_feuille: 'individuelle_entreprise',
          stagiaire_id: selectedStagiaire,
          stagiaire_nom: stagNom,
          date_debut: new Date().toISOString().split('T')[0],
          entreprise_id: user.user_id,
          entreprise_nom: user.nom || 'Entreprise',
          format_export: format
        }
      });
    } catch (error) {
      toast.error(`Erreur lors du téléchargement ${format.toUpperCase()}`);
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'present':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full" data-testid="badge-present">Présent</span>;
      case 'absent':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full" data-testid="badge-absent">Absent</span>;
      case 'retard':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full" data-testid="badge-retard">Retard</span>;
      case 'excuse':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full" data-testid="badge-excuse">Excusé</span>;
      case 'conge':
      case 'conges':
        return <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full" data-testid="badge-conge">Congé</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">—</span>;
    }
  };

  const getLieuBadge = (lieu) => {
    if (lieu === 'entreprise') {
      return <span className="px-2 py-1 bg-blue-50 text-blue-600 text-xs rounded-full flex items-center gap-1"><Building2 className="h-3 w-3" />Entreprise</span>;
    }
    return <span className="px-2 py-1 bg-green-50 text-green-600 text-xs rounded-full flex items-center gap-1"><GraduationCap className="h-3 w-3" />Centre</span>;
  };

  const getCreatedByLabel = (presence) => {
    const creator = presence.created_by || '';
    if (presence.entreprise_id) return 'Entreprise';
    if (presence.formateur_id) return 'Formateur';
    // Check created_by against known roles
    const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
    if (stag?.tuteur_id === creator || stag?.tuteur_id_2 === creator) return 'Tuteur';
    if (stag?.entreprise_id === creator || stag?.entreprise_id_2 === creator) return 'Entreprise';
    if (creator === 'centre') return 'Centre';
    return 'Formateur';
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  // Stats - use ALL presences (not filtered)
  const statsPresence = presences.reduce((acc, p) => {
    acc[p.statut] = (acc[p.statut] || 0) + 1;
    return acc;
  }, {});

  const statsLieu = presences.reduce((acc, p) => {
    const lieu = p.lieu || 'centre';
    acc[lieu] = (acc[lieu] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="presence-entreprise">
      <div>
        <h1 className="text-4xl font-display font-bold text-[#0F172A] mb-2">Suivi des Présences</h1>
        <p className="text-slate-600">Consultez toutes les présences de vos stagiaires (centre et entreprise)</p>
      </div>

      {/* Sélection Stagiaire */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex flex-col md:flex-row md:items-end gap-4">
          <div className="flex-1">
            <Label htmlFor="stagiaire">Stagiaire</Label>
            <select
              id="stagiaire"
              value={selectedStagiaire}
              onChange={(e) => setSelectedStagiaire(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-stagiaire"
            >
              {stagiaires.length === 0 ? (
                <option value="">Aucun stagiaire assigné</option>
              ) : (
                stagiaires.map((s) => (
                  <option key={s.user_id} value={s.user_id}>
                    {s.user_info?.prenom} {s.user_info?.nom} - {s.centre_formation}
                  </option>
                ))
              )}
            </select>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => downloadPresenceIndividuelle('pdf')} className="bg-[#8DC63F] hover:bg-[#7db336]">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button onClick={() => downloadPresenceIndividuelle('excel')} variant="outline" className="border-green-500 text-green-600 hover:bg-green-50">
              <FileText className="h-4 w-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {currentStagiaire && (
          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-[#8DC63F]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">
                    {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
                  </p>
                  <p className="text-sm text-slate-600">
                    {currentStagiaire.centre_formation} - {currentStagiaire.statut}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 text-center flex-wrap">
                <div>
                  <p className="text-xl font-bold text-green-600">{statsPresence.present || 0}</p>
                  <p className="text-xs text-slate-600">Présent</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-red-600">{statsPresence.absent || 0}</p>
                  <p className="text-xs text-slate-600">Absent</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-orange-600">{statsPresence.retard || 0}</p>
                  <p className="text-xs text-slate-600">Retard</p>
                </div>
                <div className="border-l border-slate-200 pl-4">
                  <p className="text-xl font-bold text-blue-600">{statsLieu.entreprise || 0}</p>
                  <p className="text-xs text-slate-600">En entreprise</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-green-600">{statsLieu.centre || 0}</p>
                  <p className="text-xs text-slate-600">Au centre</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Historique Présences */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-[#8DC63F]" />
            <h2 className="text-xl font-display font-semibold text-[#0F172A]">Historique des Présences</h2>
            <span className="text-sm text-slate-500">({filteredPresences.length})</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setFilterLieu('tous')}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filterLieu === 'tous' ? 'bg-[#8DC63F] text-white border-[#8DC63F]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid="filter-tous"
            >
              Tous ({presences.length})
            </button>
            <button
              onClick={() => setFilterLieu('centre')}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filterLieu === 'centre' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid="filter-centre"
            >
              Centre ({presences.filter(p => (p.lieu || 'centre') === 'centre').length})
            </button>
            <button
              onClick={() => setFilterLieu('entreprise')}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filterLieu === 'entreprise' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
              data-testid="filter-entreprise"
            >
              Entreprise ({presences.filter(p => p.lieu === 'entreprise').length})
            </button>
          </div>
        </div>

        {filteredPresences.length === 0 ? (
          <p className="text-slate-500 text-center py-8" data-testid="no-presences">Aucune présence enregistrée</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm" data-testid="presences-table">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Période</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Lieu</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Statut</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Notifié par</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Motif</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredPresences.sort((a, b) => new Date(b.date) - new Date(a.date)).map((presence, idx) => (
                  <tr key={idx} className={`hover:bg-slate-50 ${presence.statut === 'absent' ? 'bg-red-50/30' : presence.statut === 'retard' ? 'bg-orange-50/30' : ''}`}>
                    <td className="px-4 py-3">{new Date(presence.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3">{presence.periode === 'matin' ? 'Matin' : presence.periode === 'apres_midi' || presence.periode === 'apres-midi' ? 'Après-midi' : presence.periode === 'journee' ? 'Journée' : presence.periode}</td>
                    <td className="px-4 py-3">{getLieuBadge(presence.lieu)}</td>
                    <td className="px-4 py-3">{getStatusBadge(presence.statut)}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-1 text-xs text-slate-600">
                        <UserCheck className="h-3 w-3" />
                        {getCreatedByLabel(presence)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{presence.motif || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PresenceEntreprise;
